import type { DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import { z } from "zod";
import type { PlanTableFormat } from "../types/planning/plan.type";

/**
 * Determines drop header flag, the ID of the shipment after which the drop occurred,
 * and the subsequent stop sequence.
 */
export const getDropMetrics = (
  plan: PlanTableFormat,
  location: DragLocationHistory,
  destinationIndex: number,
  isDroppedOnHeader: boolean
): {
  isDroppedOnHeader: boolean;
  afterShipmentId?: number;
  afterRouteStop: number | null;
} => {
  const targetData = location.current.dropTargets[0].data;
  const edgeSymbol = Object.getOwnPropertySymbols(targetData)[0];

  const edge = z
    .enum(["top", "bottom"])
    .parse(targetData[edgeSymbol] ?? "bottom");

  const sorted = [...plan.shipments].sort((a, b) => a.stopSeq - b.stopSeq);

  const idx = Number(destinationIndex);
  const isTrap = plan.type === "delivery_trap";

  const resolveResult = (shipment?: (typeof sorted)[number]) => ({
    isDroppedOnHeader,
    afterShipmentId: isTrap ? undefined : shipment?.id,
    afterRouteStop: isTrap ? null : shipment?.stopSeq ?? null,
  });

  // Header or first slot
  if (isDroppedOnHeader || !idx) {
    return {
      isDroppedOnHeader,
      afterShipmentId: undefined,
      afterRouteStop: null,
    };
  }

  // TOP edge logic
  if (edge === "top") {
    if (idx === 1) {
      return resolveResult(undefined);
    }

    const prevIdx = Math.min(idx - 2, sorted.length - 1);
    return resolveResult(sorted[prevIdx]);
  }

  // BOTTOM edge logic
  const prevIdx = Math.min(idx - 1, sorted.length - 1);

  return resolveResult(sorted[prevIdx]);
};
